import type {
  EstateDocumentItem,
  EstateDocumentStatus,
  EstateDocumentType,
  EstateItem,
} from "../api-modules/estate.js";

/**
 * Pure helpers for the estate readiness UI. Phase 9 (2026-05-08).
 */

/**
 * The 7 canonical estate documents Hearth tracks for readiness. Order
 * matches the typical drafting sequence (will + trust first, then
 * healthcare, then financial-control documents).
 */
export const ESTATE_DOCUMENT_TYPES: ReadonlyArray<{
  type: EstateDocumentType;
  label: string;
  description: string;
}> = [
  {
    type: "will",
    label: "Last will & testament",
    description:
      "Distributes property + names guardians. The minimum-viable estate document for a household with minor children.",
  },
  {
    type: "revocable_trust",
    label: "Revocable living trust",
    description:
      "Avoids probate for assets retitled into the trust + provides incapacity planning. Optional but common alongside a pour-over will.",
  },
  {
    type: "healthcare_proxy",
    label: "Healthcare proxy",
    description:
      "Names someone to make medical decisions if you can't. NY-specific form required for NY residents.",
  },
  {
    type: "hipaa_release",
    label: "HIPAA release",
    description:
      "Authorizes named individuals to access your medical records — separate from the healthcare proxy.",
  },
  {
    type: "financial_poa",
    label: "Financial power of attorney",
    description:
      "Authorizes someone to act on your behalf for financial matters during incapacity.",
  },
  {
    type: "guardianship_designation",
    label: "Guardianship designation",
    description:
      "Names interim guardians for minor children pending court appointment. Some states require a separate document from the will.",
  },
  {
    type: "advance_directive",
    label: "Advance directive (living will)",
    description:
      "End-of-life care preferences (life support, resuscitation). Often combined with the healthcare proxy.",
  },
];

const STALE_REVIEW_DAYS = 730;

export interface ReadinessSummary {
  total: number;
  needed: number;
  drafted: number;
  executed: number;
  reviewed: number;
  staleReviewCount: number;
}

export function summarizeReadiness(
  items: EstateItem[],
): ReadinessSummary {
  const docs = items.filter(
    (i): i is EstateDocumentItem => i.item_type === "document",
  );
  const summary: ReadinessSummary = {
    total: ESTATE_DOCUMENT_TYPES.length,
    needed: 0,
    drafted: 0,
    executed: 0,
    reviewed: 0,
    staleReviewCount: 0,
  };
  // Build a map keyed by document_type — only the latest row per type
  // counts toward the readiness summary (multiple drafts of the same
  // doc are unusual but possible during a renewal flow).
  const latestByType = new Map<EstateDocumentType, EstateDocumentItem>();
  for (const d of docs) {
    const cur = latestByType.get(d.document_type);
    if (!cur || d.updated_at > cur.updated_at) {
      latestByType.set(d.document_type, d);
    }
  }
  for (const t of ESTATE_DOCUMENT_TYPES) {
    const d = latestByType.get(t.type);
    if (!d) {
      summary.needed += 1;
      continue;
    }
    summary[d.status] += 1;
    if (d.status === "reviewed" && isStaleReview(d.last_reviewed_at)) {
      summary.staleReviewCount += 1;
    }
  }
  return summary;
}

export function isStaleReview(lastReviewedAt: string | undefined): boolean {
  if (!lastReviewedAt) return true;
  const ms = Date.parse(lastReviewedAt);
  if (Number.isNaN(ms)) return true;
  const ageDays = (Date.now() - ms) / (1000 * 60 * 60 * 24);
  return ageDays > STALE_REVIEW_DAYS;
}

/**
 * Map document_type → latest row (for the per-tile rendering on /estate).
 */
export function latestDocumentsByType(
  items: EstateItem[],
): Map<EstateDocumentType, EstateDocumentItem> {
  const out = new Map<EstateDocumentType, EstateDocumentItem>();
  for (const i of items) {
    if (i.item_type !== "document") continue;
    const cur = out.get(i.document_type);
    if (!cur || i.updated_at > cur.updated_at) {
      out.set(i.document_type, i);
    }
  }
  return out;
}

export const STATUS_LABEL: Record<EstateDocumentStatus, string> = {
  needed: "Not yet drafted",
  drafted: "Drafted",
  executed: "Signed + executed",
  reviewed: "Recently reviewed",
};

/**
 * Allowed forward transitions for the document state machine. Mirrors
 * the server-side enforcement in the EstateFn update.ts handler.
 */
export const STATUS_NEXT: Record<EstateDocumentStatus, EstateDocumentStatus[]> =
  {
    needed: ["drafted"],
    drafted: ["executed", "needed"],
    executed: ["reviewed"],
    reviewed: ["executed"],
  };
